import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import HeaderVariant from '../common/HeaderVariant';
import Footer from '../Footer/Footer';
import { Link } from 'react-router-dom';

const SuccessAnimation = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <motion.div
      initial={{ scale: 0.5 }}
      animate={{ scale: 1 }}
      exit={{ scale: 0.5 }}
      className="bg-white text-black p-8 rounded-lg shadow-xl text-center"
    >
      <div className="w-16 h-16 bg-green-500 rounded-full mx-auto mb-4 flex items-center justify-center">
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-2xl font-bold mb-2">We will get back to you</h3>
    </motion.div>
  </motion.div>
);

const BusinessForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    businessName: '',
    industry: '',
    otherIndustry: '',
    businessAddress: '',
    businessPhone: '',
    contactPerson: '',
    contactPosition: '',
    otherPosition: '',
    contactEmail: '',
    contactPhone: '',
    services: [],
    customSolutions: '',
    specificNeeds: '',
    estimatedDrivers: '',
    preferredStartDate: '',
    heardAboutUsSource: '',
    heardAboutUsDetail: '',
    personalizedNote: '',
    disclaimer: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSubmit = {
        ...formData,
        services: formData.services.join(", "),
        formType: 'business'
      };

      const response = await fetch('https://script.google.com/macros/s/AKfycbzi2XgDwgIgDKXjCnB2NXn4bmh0ff5uTVCcGcbPP-ZWltYflz9kFtQkqy8q9eUVZv0jhw/exec', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formDataToSubmit)
      });

      const emailFormData = new FormData();
      emailFormData.append('_subject', 'New Business Inquiry');
      emailFormData.append('_template', 'table');
      emailFormData.append('_captcha', 'false');
      emailFormData.append('_replyto', 'concierge@jacks.com.co');
      emailFormData.append('_from', 'concierge@jacks.com.co');
      
      for (const [key, value] of Object.entries(formData)) {
        emailFormData.append(key, value);
      }

      await fetch('https://formsubmit.co/concierge@jacks.com.co', {
        method: 'POST',
        body: emailFormData
      });

      setSubmitStatus('success');
      setShowSuccess(true);
      
      setTimeout(() => {
        setShowSuccess(false);
      }, 2000);
      
      setFormData({
        businessName: '',
        industry: '',
        otherIndustry: '',
        businessAddress: '',
        businessPhone: '',
        contactPerson: '',
        contactPosition: '',
        otherPosition: '',
        contactEmail: '',
        contactPhone: '',
        services: [],
        customSolutions: '',
        specificNeeds: '',
        estimatedDrivers: '',
        preferredStartDate: '',
        heardAboutUsSource: '',
        heardAboutUsDetail: '',
        personalizedNote: '',
        disclaimer: ''
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  const inputClassName = "w-full bg-[#1A1A1A] text-white px-4 py-3 rounded-md border border-gray-800 focus:border-white focus:ring-1 focus:ring-white transition-colors";
  const dateInputClassName = `${inputClassName} [&::-webkit-calendar-picker-indicator]:filter [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:hover:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="businessName" className="block text-sm font-medium text-gray-300 mb-2">
                  Business Name
                </label>
                <input
                  type="text"
                  name="businessName"
                  id="businessName"
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  required
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="businessPhone" className="block text-sm font-medium text-gray-300 mb-2">
                  Business Phone
                </label>
                <input
                  type="tel"
                  name="businessPhone"
                  id="businessPhone"
                  value={formData.businessPhone}
                  onChange={(e) => setFormData({ ...formData, businessPhone: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="(123) 456-7890"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="industry" className="block text-sm font-medium text-gray-300 mb-2">
                  Industry
                </label>
                <select
                  name="industry"
                  id="industry"
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  required
                  className={inputClassName}
                >
                  <option value="">Select Industry</option>
                  <option value="Car Rental">Car Rental</option>
                  <option value="Dealership">Dealership</option>
                  <option value="Corporate Fleet">Corporate Fleet</option>
                  <option value="Hospitality">Hospitality</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {formData.industry === 'other' && (
                <div>
                  <label htmlFor="otherIndustry" className="block text-sm font-medium text-gray-300 mb-2">
                    Other Industry
                  </label>
                  <input
                    type="text"
                    name="otherIndustry"
                    id="otherIndustry"
                    value={formData.otherIndustry}
                    onChange={(e) => setFormData({ ...formData, otherIndustry: e.target.value })}
                    required
                    className={inputClassName}
                    placeholder="Please specify your industry"
                  />
                </div>
              )}
            </div>

            <div>
              <label htmlFor="businessAddress" className="block text-sm font-medium text-gray-300 mb-2">
                Business Address
              </label>
              <input
                type="text"
                name="businessAddress"
                id="businessAddress"
                value={formData.businessAddress}
                onChange={(e) => setFormData({ ...formData, businessAddress: e.target.value })}
                required
                className={inputClassName}
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.businessName || !formData.businessPhone || !formData.industry || !formData.businessAddress}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(!formData.businessName || !formData.businessPhone || !formData.industry || !formData.businessAddress) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                Next
              </button>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Person
                </label>
                <input
                  type="text"
                  name="contactPerson"
                  id="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  required
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="contactPosition" className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Position
                </label>
                <select
                  name="contactPosition"
                  id="contactPosition"
                  value={formData.contactPosition}
                  onChange={(e) => setFormData({ ...formData, contactPosition: e.target.value })}
                  required
                  className={inputClassName}
                >
                  <option value="">Select Position</option>
                  <option value="CEO">CEO</option>
                  <option value="Manager">Manager</option>
                  <option value="HR">HR</option>
                  <option value="Operations">Operations</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {formData.contactPosition === 'other' && (
              <div>
                <label htmlFor="otherPosition" className="block text-sm font-medium text-gray-300 mb-2">
                  Other Position
                </label>
                <input
                  type="text"
                  name="otherPosition"
                  id="otherPosition"
                  value={formData.otherPosition}
                  onChange={(e) => setFormData({ ...formData, otherPosition: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="Please specify your position"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  name="contactEmail"
                  id="contactEmail"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  required
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-300 mb-2">
                  Contact Phone Number
                </label>
                <input
                  type="tel"
                  name="contactPhone"
                  id="contactPhone"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="(123) 456-7890"
                />
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="px-8 py-3 border border-white text-white rounded-md transition-all font-medium hover:bg-white/10"
              >
                Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.contactPerson || !formData.contactPosition || !formData.contactEmail || !formData.contactPhone}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(!formData.contactPerson || !formData.contactPosition || !formData.contactEmail || !formData.contactPhone) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                Next
              </button>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-4">
                What services are you interested in? (Select all that apply)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Professional Driver Services',
                  'Fleet Management Solutions',
                  'Logistics Support',
                  'Enhanced Insurance Packages',
                  'Custom Solutions'
                ].map((service) => (
                  <label key={service} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.services.includes(service)}
                      onChange={(e) => {
                        const updatedServices = e.target.checked
                          ? [...formData.services, service]
                          : formData.services.filter(s => s !== service);
                        setFormData({ ...formData, services: updatedServices });
                      }}
                      className="w-4 h-4 border border-gray-800 rounded bg-[#1A1A1A] checked:bg-white checked:border-white focus:ring-1 focus:ring-white text-black transition-colors cursor-pointer"
                    />
                    <span className="text-gray-300">{service}</span>
                  </label>
                ))}
              </div>
            </div>

            {formData.services.includes('Custom Solutions') && (
              <div>
                <label htmlFor="customSolutions" className="block text-sm font-medium text-gray-300 mb-2">
                  Please specify your custom solution needs
                </label>
                <textarea
                  name="customSolutions"
                  id="customSolutions"
                  value={formData.customSolutions}
                  onChange={(e) => setFormData({ ...formData, customSolutions: e.target.value })}
                  required
                  className={`${inputClassName} h-24`}
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="estimatedDrivers" className="block text-sm font-medium text-gray-300 mb-2">
                  Estimated Number of Drivers Required
                </label>
                <input
                  type="text"
                  name="estimatedDrivers"
                  id="estimatedDrivers"
                  value={formData.estimatedDrivers}
                  onChange={(e) => setFormData({ ...formData, estimatedDrivers: e.target.value })}
                  required
                  className={inputClassName}
                  placeholder="e.g., 5-10 drivers"
                />
              </div>

              <div>
                <label htmlFor="preferredStartDate" className="block text-sm font-medium text-gray-300 mb-2">
                  Preferred Start Date
                </label>
                <input
                  type="date"
                  name="preferredStartDate"
                  id="preferredStartDate"
                  value={formData.preferredStartDate}
                  onChange={(e) => setFormData({ ...formData, preferredStartDate: e.target.value })}
                  required
                  className={dateInputClassName}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="px-8 py-3 border border-white text-white rounded-md transition-all font-medium hover:bg-white/10"
              >
                Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={!formData.services.length || !formData.estimatedDrivers || !formData.preferredStartDate}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(!formData.services.length || !formData.estimatedDrivers || !formData.preferredStartDate) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                Next
              </button>
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <label htmlFor="specificNeeds" className="block text-sm font-medium text-gray-300 mb-2">
                Describe your specific needs or challenges
              </label>
              <textarea
                name="specificNeeds"
                id="specificNeeds"
                value={formData.specificNeeds}
                onChange={(e) => setFormData({ ...formData, specificNeeds: e.target.value })}
                required
                className={`${inputClassName} h-24`}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="heardAboutUsSource" className="block text-sm font-medium text-gray-300 mb-2">
                  How did you hear about Jacks?
                </label>
                <select
                  name="heardAboutUsSource"
                  id="heardAboutUsSource"
                  value={formData.heardAboutUsSource}
                  onChange={(e) => setFormData({ ...formData, heardAboutUsSource: e.target.value, heardAboutUsDetail: '' })}
                  required
                  className={inputClassName}
                >
                  <option value="">Select Source</option>
                  <option value="Social Media">Social Media</option>
                  <option value="Search Engine">Search Engine</option>
                  <option value="Referral">Referral</option>
                  <option value="Event">Event</option>
                  <option value="Advertisement">Advertisement</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {formData.heardAboutUsSource && formData.heardAboutUsSource !== 'other' && (
                <div>
                  <label htmlFor="heardAboutUsDetail" className="block text-sm font-medium text-gray-300 mb-2">
                    {formData.heardAboutUsSource === 'Social Media' ? 'Which platform?' :
                     formData.heardAboutUsSource === 'Referral' ? 'Who referred you?' :
                     formData.heardAboutUsSource === 'Event' ? 'Which event?' :
                     formData.heardAboutUsSource === 'Advertisement' ? 'Where did you see the ad?' :
                     'Please provide more details'}
                  </label>
                  <input
                    type="text"
                    name="heardAboutUsDetail"
                    id="heardAboutUsDetail"
                    value={formData.heardAboutUsDetail}
                    onChange={(e) => setFormData({ ...formData, heardAboutUsDetail: e.target.value })}
                    required
                    className={inputClassName}
                  />
                </div>
              )}

              {formData.heardAboutUsSource === 'other' && (
                <div>
                  <label htmlFor="heardAboutUsDetail" className="block text-sm font-medium text-gray-300 mb-2">
                    Please specify
                  </label>
                  <input
                    type="text"
                    name="heardAboutUsDetail"
                    id="heardAboutUsDetail"
                    value={formData.heardAboutUsDetail}
                    onChange={(e) => setFormData({ ...formData, heardAboutUsDetail: e.target.value })}
                    required
                    className={inputClassName}
                  />
                </div>
              )}
            </div>

            <div>
              <label htmlFor="personalizedNote" className="block text-sm font-medium text-gray-300 mb-2">
                Any additional comments or details we should know?
              </label>
              <textarea
                name="personalizedNote"
                id="personalizedNote"
                value={formData.personalizedNote}
                onChange={(e) => setFormData({ ...formData, personalizedNote: e.target.value })}
                className={`${inputClassName} h-16`}
                placeholder="Optional"
              />
            </div>

            <div className="border-t border-gray-800 pt-6">
              <div className="flex items-start mb-6">
                <div className="flex items-center h-5">
                  <input
                    id="disclaimer"
                    name="disclaimer"
                    type="checkbox"
                    required
                    checked={formData.disclaimer === "I Agree"}
                    onChange={(e) => setFormData({
                      ...formData, 
                      disclaimer: e.target.checked ? "I Agree" : ""
                    })}
                    className="w-4 h-4 border border-gray-800 rounded bg-[#1A1A1A] checked:bg-white checked:border-white focus:ring-1 focus:ring-white text-black transition-colors cursor-pointer"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="disclaimer" className="font-light text-gray-300">
                    I agree to the{' '}
                    <Link to="/terms-of-use" className="font-medium text-blue-500 hover:underline">
                      Terms and Conditions
                    </Link>{', '}
                    <Link to="/disclaimer" className="font-medium text-blue-500 hover:underline">
                      Disclaimer
                    </Link>{', '}
                    and acknowledge that my information will be used in accordance with the{' '}
                    <Link to="/privacypolicy" className="font-medium text-blue-500 hover:underline">
                      Privacy Policy
                    </Link>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={prevStep}
                className="px-8 py-3 border border-white text-white rounded-md transition-all font-medium hover:bg-white/10"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.specificNeeds || !formData.disclaimer}
                className={`px-8 py-3 bg-white text-black rounded-md transition-all font-medium
                  ${(isSubmitting || !formData.specificNeeds || !formData.disclaimer) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:scale-[0.99]'}
                `}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Your Inquiry'}
              </button>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <HeaderVariant />
      
      <div className="relative min-h-screen bg-black">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.02] select-none pointer-events-none overflow-hidden">
          <h1 className="text-[20vw] font-bold text-white whitespace-nowrap">BUSINESS</h1>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 container mx-auto px-4 py-24 pt-32"
        >
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16 pt-24">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Jacks Business Services Inquiry Form
              </h1>
              <p className="text-gray-400 text-lg max-w-3xl mx-auto">
                Welcome to Jacks! We're here to help your business save money, enhance operations, and deliver exceptional customer experiences.
              </p>
            </div>

            <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8">
              <div className="flex justify-between mb-8">
                {[1, 2, 3, 4].map((step) => (
                  <div
                    key={step}
                    className={`flex items-center ${step < 4 ? 'flex-1' : ''}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors
                        ${currentStep >= step ? 'bg-white text-black' : 'bg-gray-800 text-gray-400'}
                      `}
                    >
                      {step}
                    </div>
                    {step < 4 && (
                      <div
                        className={`flex-1 h-px mx-2 transition-colors
                          ${currentStep > step ? 'bg-white' : 'bg-gray-800'}
                        `}
                      />
                    )}
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                <AnimatePresence mode="wait">
                  {renderStep()}
                </AnimatePresence>
              </form>
            </div>
          </div>
        </motion.div>
      </div>

      <Footer />

      <AnimatePresence>
        {showSuccess && <SuccessAnimation />}
      </AnimatePresence>
    </div>
  );
};

export default BusinessForm; 